import React, { useCallback, useEffect, useState } from 'react';
import { getUserDomains, unlockDomainForUser, sendSiteLiveEmailForUser, sendDomainReservedEmailForUser, inspectDomainResidues, testDomainCleanup, mapDomainError } from '../../services/domainService';

import { formatRelativeDate, toDateValue } from '../../utils/relativeTime';
import { DOMAIN_STATUS_LABELS, StatusPill } from './domainStatus';
import DomainDetails from './DomainDetails';
import RegisterDomainModal from './RegisterDomainModal';
import ConfirmDialog from '../ui/ConfirmDialog';

/**
 * Libellés des familles de résidus mesurées par le serveur (`domain-cleanup-policy.js`).
 * Ce sont les 9 familles de la liste FERMÉE : l'écran n'en invente aucune, il TRADUIT.
 */
const RESIDUE_LABELS = {
  ORPHAN_HANDLE: 'Handle OpenProvider orphelin',
  ORPHAN_HANDLE_UNPROVEN: 'Handle non traçable (information)',
  ORPHAN_HANDLE_ABSENT: 'Handle déjà absent chez le registrar (information)',
  NGINX_VHOST: 'Bloc nginx du domaine',
  NGINX_CONFLICT_OTHER_USER: 'server_name porté par un AUTRE client (alerte)',
  CERTIFICATE: 'Certificat TLS',
  PROVISIONING_WORKER: 'Worker SSL',
  DNS_ZONE: 'Zone DNS (réservée)',
  FIRESTORE_TRACE: 'Fiche de suivi (trace, conservée)'
};

/** Libellé lisible d'une famille de résidu (le code serveur reste visible si inconnu). */
const residueLabel = (type) => RESIDUE_LABELS[type] || type || '—';

/**
 * Mention affichée dans le rapport : « ce qui serait supprimé » (simulation) ou « ce qui est
 * resté » (inventaire). Elle ne décrit JAMAIS une suppression réelle : ce panneau ne sait pas
 * en déclencher (décision D3c-20).
 */
const residueHeadline = (action) => (action === 'dry-run'
  ? 'Simulation : voici ce qui SERAIT supprimé (rien n’a été supprimé).'
  : 'Inventaire en lecture seule : rien n’a été supprimé, rien n’a été modifié.');

/**
 * Libellés des verdicts rendus par le serveur — DEUX vocabulaires, selon l'appel :
 * `inspect` rend un verdict de MESURE (`dirty` / `clean`), la simulation et l'exécution rendent le
 * verdict du PLAN (`pending` / `clean` / `blocked`).
 *
 * `pending` veut dire « des suppressions sont PLANIFIÉES », jamais « en attente d'un tiers » : sans
 * cette traduction, le support lit l'inverse de la décision du serveur (relevé du 03/10/2026).
 * Le code brut reste affiché entre parenthèses — l'écran TRADUIT, il ne réécrit pas le serveur.
 */
const CLEANUP_VERDICT_LABELS = {
  clean: 'rien à supprimer (terrain propre)',
  dirty: 'résidus à traiter',
  pending: 'suppressions planifiées',
  blocked: 'protégé — décision humaine requise'
};

/** Libellé lisible d'un verdict (le code serveur reste visible si inconnu). */
const verdictLabel = (verdict) => CLEANUP_VERDICT_LABELS[verdict] || verdict || '—';

/**
 * Libellés des MOTIFS rendus par la politique serveur (`domain-cleanup-policy.js`) — liste FERMÉE :
 * `SKIP_REASON` (pourquoi un résidu MESURÉ n'est pas supprimé) et `BLOCK_REASON` (présomption de
 * vie ⇒ incident support). L'écran TRADUIT ces codes, il n'en invente aucun.
 *
 * Sans cette traduction, le support voit « 4 mesurés » puis « 2 supprimables » sans pouvoir
 * réconcilier les deux nombres (relevé du 03/10/2026) : ici il lit, pour chaque écart, sa RAISON.
 */
const PLAN_REASON_LABELS = {
  // Écarts de politique (SKIP_REASON)
  'msd-handle': 'handle Mon Site Direct (protégé par valeur)',
  referenced: 'référencé par un domaine du compte',
  unproven: 'orphelin non prouvé (information support)',
  'already-absent': 'déjà absent chez le registrar',
  'no-proof': 'aucune preuve (portée fermée)',
  'live-domain': 'domaine vivant (résidu attendu)',
  'out-of-scope': 'hors de la portée de la publication',
  reserved: 'zone DNS réservée (étape 5)',
  trace: 'fiche de suivi conservée (trace)',
  'unknown-type': 'famille inconnue (jamais supprimable)',
  unreadable: 'identité illisible',
  // Blocages (BLOCK_REASON) — présomption de vie : main humaine
  'handle-still-owner': 'le handle est encore l’owner du domaine',
  'registrar-refused': 'refus du registrar (objet encore référencé)',
  'other-user': 'server_name porté par un AUTRE client'
};

/** Libellé lisible d'un motif de plan (le code serveur reste visible si inconnu). */
const reasonLabel = (reason) => PLAN_REASON_LABELS[reason] || reason || '—';

/**
 * « Ce qui SERAIT supprimé » — lu sur le PLAN (`plan.deletable`), JAMAIS sur `removed[]`.
 *
 * `removed[]` est le journal d'une exécution RÉELLE : en simulation il est vide par construction.
 * Le lire faisait écrire « Aucun résidu supprimable : le terrain est déjà propre. » juste sous un
 * verdict `pending` qui venait précisément de planifier des suppressions (relevé du 03/10/2026 :
 * 4 résidus mesurés, dont 2 supprimables ⇒ l'écran affirmait le contraire du serveur).
 * @param {Object} props
 * @param {Object} props.report - Rapport rendu par la callable (`inspect` ou `dry-run`)
 */
const ResidueOutcome = ({ report }) => {
  const list = (entries) => entries
    .map((entry) => `${residueLabel(entry.type)} (${entry.id || entry.target || '—'})`)
    .join(', ');
  const removed = Array.isArray(report.removed) ? report.removed : [];
  const planned = (report.plan && Array.isArray(report.plan.deletable)) ? report.plan.deletable : [];

  if (removed.length > 0) {
    return (
      <p className="text-xs text-base-content/80 mt-2">
        {`SUPPRIMÉ (${removed.length}) : ${list(removed)}`}
      </p>
    );
  }

  if (planned.length > 0) {
    return (
      <p className="text-xs text-base-content/80 mt-2">
        {`SERAIT supprimé (${planned.length}) : ${list(planned)}`}
      </p>
    );
  }

  return (
    <p className="text-xs text-base-content/80 mt-2">
      {report.verdict === 'clean'
        ? 'Aucun résidu supprimable : le terrain est déjà propre.'
        : 'Aucune suppression planifiée — voir le verdict et les motifs ci-dessus.'}
    </p>
  );
};

/**
 * « Pourquoi 4 mesurés pour 2 supprimables ? » — le BILAN de la politique, lu SUR LE PLAN.
 *
 * L'inventaire et le plan ne comptent PAS la même chose : `plan.counts` est la somme de contrôle
 * (`residues = deletable + blocked + skipped`) et `plan.skipped` porte le motif de chaque résidu
 * ÉCARTÉ (zone réservée, fiche-trace, handle protégé…). Sans ce bloc, le support lit deux nombres
 * différents sans pouvoir les réconcilier (relevé du 03/10/2026 : 4 résidus mesurés, 2 supprimables).
 *
 * Ce bloc ne décide rien : il ne fait que MONTRER le classement du serveur (aucun chemin de cet
 * écran ne peut supprimer — décision D3c-20).
 * @param {Object} props
 * @param {Object} props.report - Rapport rendu par la callable (`inspect` ou `dry-run`)
 */
const ResiduePolicySummary = ({ report }) => {
  const counts = (report.plan && report.plan.counts) || null;
  const skipped = (report.plan && Array.isArray(report.plan.skipped)) ? report.plan.skipped : [];
  if (!counts || typeof counts.residues !== 'number') {
    return null;
  }

  return (
    <div className="mt-1">
      <p className="text-xs text-base-content/60">
        {`Bilan de la politique : ${counts.residues} mesuré(s) → ${counts.deletable} supprimable(s), `
          + `${counts.skipped || 0} écarté(s), ${counts.blocked || 0} bloqué(s).`}
      </p>
      {skipped.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {skipped.map((entry, index) => (
            <li
              key={`${entry.type}-${entry.id || index}-${entry.reason || ''}`}
              className="text-xs text-base-content/60"
            >
              ◦ écarté — {residueLabel(entry.type)}{' '}
              <span className="font-mono">{entry.id || '—'}</span> : {reasonLabel(entry.reason)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * Onglet « Domaine » de la fiche client (msd_admin).
 * Liste les domaines du client avec détail dépliable (DomainDetails) et ouvre la
 * modale de réservation pour le compte du client (RegisterDomainModal).
 * @param {Object} props
 * @param {string} props.uid - uid du client
 * @param {Object} props.client - Document user mappé (getClientDetail)
 */
const DomainTab = ({ uid, client }) => {
  const [domains, setDomains] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [unlockTarget, setUnlockTarget] = useState(null);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [mailTarget, setMailTarget] = useState(null); // domaine visé par l'email ② manuel
  const [isSendingMail, setIsSendingMail] = useState(false);
  const [reservedMailTarget, setReservedMailTarget] = useState(null); // domaine visé par l'email ① manuel
  const [isSendingReservedMail, setIsSendingReservedMail] = useState(false);

  const [residues, setResidues] = useState(null); // { domainId, action, report } — inventaire / simulation
  const [isResidueBusy, setIsResidueBusy] = useState(false);
  const [notice, setNotice] = useState(null); // { type: 'success' | 'warning' | 'error', text }

  /** Date du dernier email ② « site en ligne » envoyé (launchState.liveEmailSentAt), ou null. */
  const liveEmailSentAt = (domain) => toDateValue(domain?.launchState?.liveEmailSentAt);

  /** L'email ① « domaine réservé » a-t-il déjà été envoyé ? (marqueur serveur `notificationSent`) */
  const reservedEmailSent = (domain) => domain?.notificationSent === true;

  /** Date du dernier email ① envoyé (`notificationTimestamp`), ou null si non tracée. */
  const reservedEmailSentAt = (domain) => toDateValue(domain?.notificationTimestamp);


  const load = useCallback(async () => {
    if (!uid) return;
    setIsLoading(true);
    setLoadError('');
    try {
      const list = await getUserDomains(uid);
      setDomains(list);
    } catch (error) {
      console.warn('DomainTab: impossible de charger les domaines du client', error?.message);
      setLoadError('Impossible de charger les domaines de ce client.');
    } finally {
      setIsLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    load();
  }, [load]);

  const hasActiveDomain = domains.some((domain) => domain.status === 'active');
  const hasBlockedDomain = domains.some((domain) => domain.status === 'registration_failed');
  const canReserve = !hasActiveDomain && !hasBlockedDomain;

  const toggleDetails = (id) => setExpandedId(expandedId === id ? null : id);

  const handleRegistered = useCallback(() => {
    setIsRegisterOpen(false);
    load();
  }, [load]);

  const handleUnlockConfirm = async () => {
    if (!unlockTarget) return;
    setIsUnlocking(true);
    setNotice(null);
    try {
      const result = await unlockDomainForUser(uid, unlockTarget.id);
      setNotice({
        type: 'success',
        text: result.message || `Verrou supprimé pour ${unlockTarget.domainName}. Une nouvelle réservation est possible.`
      });
      setUnlockTarget(null);
      setExpandedId(null);
      await load();
    } catch (error) {
      const mapped = mapDomainError(error);
      setNotice({ type: 'error', text: mapped.message || 'Impossible de déverrouiller le domaine.' });
      setUnlockTarget(null);
    } finally {
      setIsUnlocking(false);
    }
  };

  /**
   * Envoi MANUEL de l'email ② « votre site est en ligne » (décision D3c-23).
   * L'email est envoyé par l'ADMIN, jamais par un job : `force: true` est transmis au serveur.
   */
  const handleSendSiteLiveEmail = async () => {
    if (!mailTarget) return;
    setIsSendingMail(true);
    setNotice(null);
    try {
      const result = await sendSiteLiveEmailForUser(uid, mailTarget.id);
      const warnings = Array.isArray(result.warnings) ? result.warnings.map((w) => w.message) : [];
      setNotice({
        type: warnings.length ? 'warning' : 'success',
        text: `${result.resend ? 'Email « site en ligne » RENVOYÉ' : 'Email « site en ligne » envoyé'} à ${result.sentTo || "l'adresse du client"} pour ${result.domainName || mailTarget.domainName}.${warnings.length ? ` ⚠ ${warnings.join(' ')}` : ''}`
      });
      setMailTarget(null);
      await load();
    } catch (error) {
      const mapped = mapDomainError(error);
      setNotice({ type: 'error', text: mapped.message || "Impossible d'envoyer l'email « site en ligne »." });
      setMailTarget(null);
    } finally {
      setIsSendingMail(false);
    }
  };

  /**
   * Envoi MANUEL de l'email ① « votre domaine est réservé » (lot I2c).
   * Même geste explicite que l'email ② (`force: true` transmis au serveur) : l'envoi automatique du
   * trigger reste INCHANGÉ — cet outil ne fait que combler le cas où le client ne l'a jamais reçu.
   */
  const handleSendDomainReservedEmail = async () => {
    if (!reservedMailTarget) return;
    setIsSendingReservedMail(true);
    setNotice(null);
    try {
      const result = await sendDomainReservedEmailForUser(uid, reservedMailTarget.id);
      const warnings = Array.isArray(result.warnings) ? result.warnings.map((w) => w.message) : [];
      setNotice({
        type: warnings.length ? 'warning' : 'success',
        text: `${result.resend ? 'Email ① « domaine réservé » RENVOYÉ' : 'Email ① « domaine réservé » envoyé'} à ${result.sentTo || "l'adresse du client"} pour ${result.domainName || reservedMailTarget.domainName}.${warnings.length ? ` ⚠ ${warnings.join(' ')}` : ''}`
      });
      setReservedMailTarget(null);
      await load();
    } catch (error) {
      const mapped = mapDomainError(error);
      setNotice({ type: 'error', text: mapped.message || "Impossible d'envoyer l'email ① « domaine réservé »." });
      setReservedMailTarget(null);
    } finally {
      setIsSendingReservedMail(false);
    }
  };

  /** Message de confirmation de l'envoi manuel de l'email ① (état factuel connu de l'admin). */
  const reservedMailConfirmMessage = (domain) => {
    if (!domain) return '';
    const sentAt = reservedEmailSentAt(domain);
    const target = `« ${domain.domainName} »`;
    if (reservedEmailSent(domain)) {
      return `Un email « domaine réservé » a DÉJÀ été envoyé pour ${target}${sentAt ? ` (${sentAt.toLocaleString('fr-FR')})` : ''}. Le renvoyer au client ?`;
    }
    return `Envoyer à ${client?.email || "l'adresse du client"} l'email « votre domaine est réservé » pour ${target} ?`;
  };

  /** Message de confirmation de l'envoi manuel (rappelle l'état factuel connu de l'admin). */

  const mailConfirmMessage = (domain) => {
    if (!domain) return '';
    const sentAt = liveEmailSentAt(domain);
    const target = `« ${domain.domainName} »`;
    if (sentAt) {
      return `Un email « site en ligne » a DÉJÀ été envoyé pour ${target} (${sentAt.toLocaleString('fr-FR')}). Le renvoyer au client ?`;
    }
    return `Envoyer à ${client?.email || "l'adresse du client"} l'email « votre site est en ligne » pour ${target} ? Vérifiez d'abord que le site répond (https://${domain.domainName}).`;
  };

  /**
   * RÉSIDUS (étape 3b.5, décision D3b-14) — inventaire en LECTURE SEULE, ou simulation.
   *
   * `mode === 'inspect'` : la callable ne reçoit AUCUN drapeau ⇒ le serveur mesure et qualifie
   * (`deletable` / `blocked` / `skipped`), sans rien écrire, chez personne.
   * `mode === 'dry-run'` : `dryRun: true` ⇒ le serveur exécute le PLAN en simulation et rend ce qui
   * SERAIT supprimé. Aucun bouton ne porte `confirm: true` : un nettoyage réel n'est pas un geste
   * du panneau (décision D3c-20).
   * @param {Object} domain - Document domaine affiché
   * @param {'inspect'|'dry-run'} mode - Ce que l'admin demande
   */
  const handleResidues = async (domain, mode) => {
    if (!domain) return;
    setIsResidueBusy(true);
    setNotice(null);
    setResidues(null);
    try {
      const target = { domainId: domain.id, domainName: domain.domainName };
      const report = mode === 'dry-run'
        ? await testDomainCleanup(uid, target)
        : await inspectDomainResidues(uid, target);
      setResidues({ domainId: domain.id, action: mode, report });
    } catch (error) {
      const mapped = mapDomainError(error);
      setNotice({
        type: 'error',
        text: mapped.message || (mode === 'dry-run'
          ? 'Impossible de simuler le nettoyage de ce domaine.'
          : 'Impossible de lire les résidus de ce domaine.')
      });
    } finally {
      setIsResidueBusy(false);
    }
  };

  const reservationTooltip = hasActiveDomain
    ? 'Ce client possède déjà un domaine actif (1 seul par abonnement).'
    : hasBlockedDomain
      ? 'Un enregistrement est en échec (verrou anti-doublon) : détaillez le domaine puis supprimez le blocage.'
      : 'Rechercher et réserver un nom de domaine (.fr/.com) pour ce client.';

  return (
    <div className="space-y-4">
      {/* En-tête + action */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h4 className="font-semibold">Domaines du client</h4>
        <button
          onClick={() => setIsRegisterOpen(true)}
          disabled={!canReserve}
          className="btn btn-sm btn-primary"
          title={reservationTooltip}
        >
          + Réserver un domaine
        </button>
      </div>

      {notice && (
        <div className={`flex items-start justify-between gap-2 rounded-lg p-3 text-sm border ${notice.type === 'success' ? 'bg-success/10 border-success/30' : notice.type === 'warning' ? 'bg-warning/10 border-warning/30' : 'bg-error/10 border-error/30'}`}>
          <p className={`${notice.type === 'success' ? 'text-success' : notice.type === 'warning' ? 'text-warning' : 'text-error'} font-medium`}>{notice.text}</p>
          <button onClick={() => setNotice(null)} className="btn btn-xs btn-ghost btn-circle" aria-label="Fermer">✕</button>
        </div>
      )}

      {hasActiveDomain && (
        <div className="bg-info/10 border border-info/30 rounded-lg p-3 text-sm text-base-content">
          Ce client a déjà un domaine actif. Un seul domaine est autorisé par abonnement.
        </div>
      )}
      {hasBlockedDomain && !hasActiveDomain && (
        <div className="bg-error/10 border border-error/30 rounded-lg p-3 text-sm text-base-content">
          ⛔ Un enregistrement précédent est en échec. Le domaine « Échec réservation » sert de verrou
          anti-doublon : il doit être supprimé par le support avant toute nouvelle tentative.
        </div>
      )}

      {/* Liste des domaines */}
      {isLoading ? (
        <div className="py-10 text-center">
          <span className="loading loading-spinner loading-md text-primary"></span>
        </div>
      ) : loadError ? (
        <div className="text-center py-8 bg-base-200/30 rounded-lg border border-dashed border-base-300">
          <p className="text-error text-sm font-medium">{loadError}</p>
          <button onClick={load} className="btn btn-outline btn-sm mt-3">Réessayer</button>
        </div>
      ) : domains.length === 0 ? (
        <div className="text-center py-12 bg-base-200/30 rounded-lg border border-dashed border-base-300">
          <p className="text-base-content font-medium">Aucun domaine enregistré pour ce client.</p>
          <p className="text-sm text-base-content/60 mt-1">
            Utilisez « + Réserver un domaine » pour rechercher un nom en .fr / .com et le réserver
            pour le compte du client.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {domains.map((domain) => {
            const isExpanded = expandedId === domain.id;
            const statusLabel = DOMAIN_STATUS_LABELS[domain.status] || domain.status || '—';
            return (
              <li key={domain.id} className="border border-base-200 rounded-lg bg-base-100 overflow-hidden">
                <div className="p-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono font-semibold text-base-content text-base">{domain.domainName}</span>
                      <StatusPill label={statusLabel} status={domain.status} />
                      {domain.status === 'active' && (
                        <span className="text-xs text-base-content/50">
                          {domain.autoRenew ? 'auto-renouvellement ON' : 'auto-renouvellement OFF'}
                        </span>
                      )}
                      {liveEmailSentAt(domain) && (
                        <span
                          className="text-xs text-base-content/50"
                          title={`Email « site en ligne » envoyé le ${liveEmailSentAt(domain).toLocaleString('fr-FR')}`}
                        >
                          ✉ ② envoyé
                        </span>
                      )}

                      {reservedEmailSent(domain) && (
                        <span
                          className="text-xs text-base-content/50"
                          title={reservedEmailSentAt(domain)
                            ? `Email « domaine réservé » envoyé le ${reservedEmailSentAt(domain).toLocaleString('fr-FR')}`
                            : 'Email « domaine réservé » déjà envoyé'}
                        >
                          ✉ ① envoyé
                        </span>
                      )}

                    </div>
                    <p className="text-xs text-base-content/50 mt-1">
                      {domain.createdAt ? `Réservé ${formatRelativeDate(domain.createdAt)}` : 'Date inconnue'}
                      {domain.providerMetadata?.expirationDate
                        ? ` · expiration ${new Date(domain.providerMetadata.expirationDate).toLocaleDateString('fr-FR')}`
                        : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {domain.status === 'registration_failed' && (
                      <button
                        onClick={() => { setNotice(null); setUnlockTarget(domain); }}
                        className="btn btn-sm btn-outline btn-error"
                        title="Supprimer le verrou d'échec pour autoriser une nouvelle tentative de réservation"
                      >
                        Déverrouiller
                      </button>
                    )}
                    {domain.status !== 'registration_failed' && (
                      <button
                        onClick={() => { setNotice(null); setMailTarget(domain); }}
                        className="btn btn-sm btn-outline btn-primary"
                        title="Envoyer (ou renvoyer) au client l'email « votre site est en ligne » — geste manuel de l'admin"
                      >
                        ✉ {liveEmailSentAt(domain) ? "Renvoyer l'email" : "Email site en ligne"}
                      </button>
                    )}

                    {domain.status !== 'registration_failed' && (
                      <button
                        onClick={() => { setNotice(null); setReservedMailTarget(domain); }}
                        className="btn btn-sm btn-outline btn-info"
                        title="Envoyer (ou renvoyer) au client l'email « votre domaine est réservé » — geste manuel de l'admin"
                      >
                        ✉ {reservedEmailSent(domain) ? "Renvoyer l'email domaine réservé" : "Email domaine réservé"}
                      </button>
                    )}

                    <button
                      onClick={() => toggleDetails(domain.id)}
                      className="btn btn-sm btn-outline"
                      aria-expanded={isExpanded}
                    >
                      {isExpanded ? 'Masquer le détail' : 'Détails'}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-base-200 bg-base-200/20 px-4 py-3">
                    <DomainDetails domain={domain} />

                    {/* Résidus (étape 3b.5, D3b-14) : inventaire en LECTURE SEULE + simulation de nettoyage */}
                    <div className="mt-4 border-t border-base-300 pt-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-base-content/60">
                          Résidus (serveur / registrar)
                        </span>
                        <button
                          onClick={() => handleResidues(domain, 'inspect')}
                          disabled={isResidueBusy}
                          className="btn btn-xs btn-outline"
                          title="Inventaire en lecture seule : mesure les résidus et dit lesquels sont supprimables"
                        >
                          🔎 Inspecter les résidus
                        </button>
                        <button
                          onClick={() => handleResidues(domain, 'dry-run')}
                          disabled={isResidueBusy}
                          className="btn btn-xs btn-outline btn-info"
                          title="Simulation : montre ce qui SERAIT supprimé — rien n'est supprimé"
                        >
                          🧪 Tester le nettoyage
                        </button>
                        {isResidueBusy && <span className="loading loading-spinner loading-xs text-primary"></span>}
                      </div>
                      <p className="text-xs text-base-content/50 mt-1">
                        Lecture seule et simulation uniquement : aucun nettoyage réel n’est déclenché
                        depuis ce panneau (décision D3c-20).
                      </p>
                      {residues && residues.domainId === domain.id && (
                        <div className="mt-3 rounded-lg border border-base-300 bg-base-100 p-3">
                          <p className="text-sm font-medium text-base-content">
                            {residueHeadline(residues.action)}
                          </p>
                          <p className="text-xs text-base-content/60 mt-1">
                            verdict : {verdictLabel(residues.report.verdict)}
                            {' · '}code : {residues.report.verdict || '—'}
                            {' · '}mode : {residues.report.plan?.mode || residues.report.mode || '—'}
                            {residues.report.proof ? ` · preuve : ${residues.report.proof}` : ''}
                          </p>

                          {Array.isArray(residues.report.residues) && residues.report.residues.length > 0 && (
                            <ul className="mt-2 space-y-1">
                              {residues.report.residues.map((entry, index) => (
                                <li
                                  key={`${entry.type}-${entry.target || index}`}
                                  className="text-xs text-base-content/80"
                                >
                                  • {residueLabel(entry.type)} — <span className="font-mono">{entry.target || '—'}</span>
                                  {entry.blocking ? ' · BLOQUANT' : (entry.severity ? ` · ${entry.severity}` : '')}
                                </li>
                              ))}
                            </ul>
                          )}

                          {/* « Ce qui SERAIT supprimé » se lit sur le PLAN (`plan.deletable`) :
                              `removed[]` n'est rempli que par une exécution RÉELLE — voir `ResidueOutcome`. */}
                          <ResidueOutcome report={residues.report} />

                          {/* Le BILAN (`plan.counts`) et les motifs d'écart (`plan.skipped`) :
                              l'écran explique l'écart entre « mesuré » et « supprimable ». */}
                          <ResiduePolicySummary report={residues.report} />

                          {Array.isArray(residues.report.blocked) && residues.report.blocked.length > 0 && (
                            <p className="text-xs text-warning mt-1">
                              Protégé par la doctrine : {residues.report.blocked.map(reasonLabel).join(', ')}
                            </p>
                          )}

                          {Array.isArray(residues.report.absent) && residues.report.absent.length > 0 && (
                            <p className="text-xs text-base-content/60 mt-1">
                              Déjà absents chez le registrar (information) : {residues.report.absent.length}
                            </p>
                          )}

                          {Array.isArray(residues.report.errors) && residues.report.errors.length > 0 && (
                            <p className="text-xs text-error mt-1">
                              Erreurs de mesure : {residues.report.errors.join(' | ')}
                            </p>
                          )}

                          <p className="text-xs text-base-content/50 mt-2">
                            Un résidu « non traçable » ou « déjà absent » est une INFORMATION, jamais une
                            suppression : la décision appartient au serveur (3 verrous), pas au panneau.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <p className="text-xs text-base-content/40">
        Sources : users/{uid}/domains (documents) · domainRegistrations (déroulé des étapes).
      </p>

      {/* Modale de réservation pour le client */}
      <RegisterDomainModal
        isOpen={isRegisterOpen}
        onClose={() => setIsRegisterOpen(false)}
        uid={uid}
        client={client}
        hasActiveDomain={hasActiveDomain}
        hasBlockedDomain={hasBlockedDomain}
        onRegistered={handleRegistered}
      />

      {/* Confirmation de l'envoi manuel de l'email ② « site en ligne » (décision D3c-23) */}
      <ConfirmDialog
        isOpen={!!mailTarget}
        title="Envoyer l'email « site en ligne » ?"
        message={mailConfirmMessage(mailTarget)}
        confirmLabel={liveEmailSentAt(mailTarget) ? "Renvoyer l'email" : "Envoyer l'email"}
        cancelLabel="Annuler"
        confirmColor={liveEmailSentAt(mailTarget) ? "warning" : "primary"}
        isProcessing={isSendingMail}
        onConfirm={handleSendSiteLiveEmail}
        onCancel={() => setMailTarget(null)}
      />

      {/* Confirmation de l'envoi manuel de l'email ① « domaine réservé » (lot I2c) */}
      <ConfirmDialog
        isOpen={!!reservedMailTarget}
        title="Envoyer l'email « domaine réservé » ?"
        message={reservedMailConfirmMessage(reservedMailTarget)}
        confirmLabel={reservedEmailSent(reservedMailTarget) ? "Renvoyer l'email" : "Envoyer l'email"}
        cancelLabel="Annuler"
        confirmColor={reservedEmailSent(reservedMailTarget) ? "warning" : "primary"}
        isProcessing={isSendingReservedMail}
        onConfirm={handleSendDomainReservedEmail}
        onCancel={() => setReservedMailTarget(null)}
      />

      {/* Confirmation de déverrouillage du verrou d'échec */}

      <ConfirmDialog
        isOpen={!!unlockTarget}
        title="Déverrouiller le domaine ?"
        message={
          unlockTarget
            ? `Supprimer le verrou d'échec de « ${unlockTarget.domainName} » ? Vérifiez d'abord chez OpenProvider que le domaine n'a pas réellement été créé. Une fois le document supprimé, une nouvelle réservation sera possible.`
            : ''
        }
        confirmLabel="Déverrouiller"
        cancelLabel="Annuler"
        confirmColor="error"
        isProcessing={isUnlocking}
        onConfirm={handleUnlockConfirm}
        onCancel={() => setUnlockTarget(null)}
      />
    </div>
  );
};

export default DomainTab;
